import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import type { QuoteStatus, Weekday } from '@hcd/domain'
import { toast } from 'sonner'
import { api } from './api'
import { reportError } from './settings'

export interface Customer {
  id: string
  name: string
  kind: 'business' | 'domestic'
  status: 'prospect' | 'client' | 'archived'
  companyNumber: string
  vatNumber: string
  registeredAddress: string
  businessDescription: string
  billingContactName: string
  billingEmail: string
  billingPhone: string
  billingAddress: string
  poReference: string
  invoiceNotes: string
  onboardingCompletedAt: string | null
  reference: string
  mainPhone: string
  mainEmail: string
  website: string
  sector: string
  accountManager: string
  source: string
  internalNotes: string
  billingCcEmail: string
  paymentTermsDays: number | null
  paymentMethod: '' | 'bacs' | 'direct_debit' | 'card' | 'cheque' | 'other'
  invoiceFrequency:
    | ''
    | 'weekly'
    | 'fortnightly'
    | 'four_weekly'
    | 'monthly'
    | 'quarterly'
    | 'per_visit'
    | 'on_completion'
  poRequired: boolean
  creditLimitPence: number | null
  quickbooksCustomerId: string
  smarttaskClientId: string
  archivedReason: string
  createdAt: string
  version: number
  updatedAt: string
}
export interface Contact {
  id: string
  customerId: string
  name: string
  jobTitle: string
  email: string
  phone: string
  isPrimary: boolean
  isBilling: boolean
  isSite: boolean
  notes: string
  version: number
}
export interface Site {
  id: string
  customerId: string
  name: string
  address: string
  postcode: string
  town: string
  county: string
  latitude: number | null
  longitude: number | null
  siteContactName: string
  siteContactPhone: string
  openingHours: string
  accessNotes: string
  parkingNotes: string
  preferredStartDate: string | null
  version: number
}
export interface Period {
  key: string
  label: string
  netPence: number
  vatPence: number
  grossPence: number
}
export interface QuoteSummary {
  id: string
  reference: string
  title: string
  status: QuoteStatus
  revision: number
  customerId: string
  customerName: string
  ownerName: string
  validUntil: string | null
  periods: Period[]
  annualValuePence: number
  sentAt: string | null
  lastViewedAt: string | null
  viewCount: number
  acceptedAt: string | null
  updatedAt: string
  version: number
  /** How the quote was started; older quotes have no mode and count as off-site. */
  mode?: 'offsite' | 'onsite'
}
export interface Line {
  id: string
  priceItemId: string | null
  description: string
  basis: 'days' | 'quantity'
  days: Weekday[]
  quantity: number
  unitName: string
  unitPricePence: number
  chargeType: 'one_off' | 'recurring'
  frequency: 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'annually' | null
  vatRateBp: number
  vatCode: string
  vatRateId: string
}
export interface QuoteEvent {
  id: string
  type: string
  actorType: string
  actorLabel: string
  detail: Record<string, unknown>
  at: string
}
export interface LibraryFile {
  documentId: string
  title: string
  category: string
  versionId: string
  versionNo: number
  originalName: string
  size: number
  mime: string
  expiresOn: string | null
}
export interface QuoteDetail extends QuoteSummary {
  photoCount?: number
  areaCount?: number
  customer: Customer
  contact: Contact | null
  site: Site | null
  owner: string
  scopeIntro: string
  scopeTasks: string[]
  notes: string
  lines: Line[]
  attachments: string[]
  totals: {
    periods: Period[]
    combined: { netPence: number; vatPence: number; grossPence: number }
    annualValuePence: number
    internal: { costPence: number; netPence: number; marginBp: number | null }
    lines: { id: string; netPence: number; vatPence: number; grossPence: number }[]
  }
  approval: {
    required: boolean
    author: string
    approvers: { id: string; name: string; email: string }[]
    approvedByName: string | null
    approvedAt: string | null
  }
  currentVersionId: string | null
  versions: {
    id: string
    revision: number
    status: string
    contentHash: string
    validUntil: string
    createdAt: string
  }[]
  acceptance: {
    signerName: string
    signerPosition: string
    signerEmail: string
    signatureType: 'typed' | 'drawn'
    signatureText: string
    signatureImage: string
    contentHash: string
    acceptedAt: string
    evidence: { ip: string; userAgent: string; acceptedAtUtc: string; agreementText: string }
  } | null
  onboarding: { submittedAt: string; submitted: Record<string, string> } | null
  events: QuoteEvent[]
  libraryAvailable: LibraryFile[]
  link?: string
  emailed?: 'queued' | 'no_email' | 'disabled' | 'not_requested'
}

export const quotesQuery = (status?: string) =>
  queryOptions({
    queryKey: ['quotes', status ?? 'all'],
    queryFn: async () =>
      (await api.get<{ items: QuoteSummary[] }>(`/quotes${status ? `?status=${status}` : ''}`))
        .items,
  })
export const quoteQuery = (id: string) =>
  queryOptions({ queryKey: ['quote', id], queryFn: () => api.get<QuoteDetail>(`/quotes/${id}`) })
export const customersQuery = (q = '') =>
  queryOptions({
    queryKey: ['customers', q],
    queryFn: async () =>
      (await api.get<{ items: Customer[] }>(`/customers${q ? `?q=${encodeURIComponent(q)}` : ''}`))
        .items,
  })
export const customerQuery = (id: string) =>
  queryOptions({
    queryKey: ['customer', id],
    queryFn: () =>
      api.get<{
        customer: Customer
        contacts: Contact[]
        sites: Site[]
        quotes: {
          id: string
          reference: string
          title: string
          status: QuoteStatus
          updatedAt: string
          periods: Period[]
        }[]
      }>(`/customers/${id}`),
  })

export function useQuoteAction(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ action, body }: { action: string; body?: unknown; success?: string }) =>
      api.post<QuoteDetail>(`/quotes/${id}/${action}`, body ?? {}),
    onSuccess: (data, vars) => {
      qc.setQueryData(quoteQuery(id).queryKey, data)
      void qc.invalidateQueries({ queryKey: ['quotes'] })
      if (vars.success) toast.success(vars.success)
    },
    onError: (e) => reportError(e, () => void qc.invalidateQueries({ queryKey: ['quote', id] })),
  })
}

export const FREQ_LABEL: Record<string, string> = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
  one_off: 'One-off',
}
export const PER: Record<string, string> = {
  weekly: 'per week',
  fortnightly: 'per fortnight',
  monthly: 'per month',
  quarterly: 'per quarter',
  annually: 'per year',
  one_off: 'one-off',
}

export const ukDate = (iso: string | null | undefined, withTime = false) =>
  iso
    ? new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
        timeZone: 'Europe/London',
      }).format(new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso))
    : '—'
